import React, { useEffect, useState } from 'react';
import {
  object,
  string,
  array,
  number,
  date,
} from 'yup';
import {
  Formik,
  Form,
  FieldArray,
} from 'formik';
import { useHistory, Link } from 'react-router-dom';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons/faTrashAlt';

import Nav from 'react-bootstrap/Nav';
import TabContainer from 'react-bootstrap/TabContainer';
import TabContent from 'react-bootstrap/TabContent';
import TabPane from 'react-bootstrap/TabPane';

import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { resolveTo, initFormValues } from '~/commons/helpers';

import FormHttpAlert from '~/commons/components/FormHttpAlert.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';
import FieldIterables from '~/commons/components/FieldIterables.jsx';
import FieldArrayIterables from '~/commons/components/FieldArrayIterables.jsx';
import ModalAlert from '~/commons/components/ModalAlert.jsx';
import BtnDel from '../../btns/BtnDel.jsx';
import FieldsCompGen from './FieldsCompGen.jsx';
import FieldsCompQtnSet, { defaultQtnSet } from './FieldsCompQtnSet.jsx';
import { useCompetitions } from '../../../contexts/Competitions';

const fileUploadUrl = 'api/quiz/competitions/files';
const schema = object({
  awardsPeriod: array().of(date()).min(2),
  refCode: string().required(),
  title: string().required(),
  subjects: array().of(
    string().required(),
  ),
  duration: number().required().max(180),
  questionSets: array().of(object({
    refCode: string().required(),
    draw: number().required().integer(),
    questions: array().of(object({
      question: string().required(),
      answer: number().required().integer(),
    })),
  })),
});
const defaults = {
  refCode: '',
  title: '',
  coverImg: null,
  description: '',
  beforeStartMsg: '',
  completedMsg: '',
  beforeOpenMsg: '',
  openedMsg: '',
  closedMsg: '',
  dates: [],
  awardsPeriod: [],
  duration: '',
  questionSets: [defaultQtnSet(0)],
};

const FormCompetition = ({
  competition,
}) => {
  const history = useHistory();
  const { refresh } = useCompetitions();
  const [locked, setLocked] = useState(null);
  const [res, req] = useHttpStatus({
    onSuccess({ data }) {
      refresh();
      if (!competition) {
        history.push(resolveTo(`../${data[0]._id}`));
      }
    },
  });
  useEffect(() => {
    if (competition) {
      fetch(`api/quiz/competitions/${competition._id}/lock`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
          // history.push(resolveTo('', row._id));
          } else {
            setLocked(data.comp.locked);
          }
        }).catch((err) => {
          console.error('err', err);
        });
      return () => {
        fetch(`api/quiz/competitions/${competition._id}/unlock`);
      };
    }
  }, []);

  return (
    <>
    <Formik
      validationSchema={schema}
      initialValues={initFormValues(defaults, competition, {
        omitIds: false,
      })}
      onSubmit={async (values, actions) => {
        if (competition) {
          try {
            const res = await fetch(`api/quiz/competitions/${competition._id}/lock?checkOnly=1`);
            const data = await res.json();
            if (!data.success) {
              setLocked(data.comp ? data.comp.locked || false : false);
              return;
            }
          } catch (e) {
            console.error('err', e);
            return;
          }
        }
        const { status } = await req({
          method: competition ? 'patch' : 'post',
          url: `api/quiz/competitions${competition ? `/${competition._id}` : ''}`,
          data: {
            ...values,
            questionSets: values.questionSets.map((s) => ({
              ...s,
              questions: s.questions.map((q) => ({
                ...q,
                choices: q.choices.filter((c) => c),
              })),
            })),
          },
        });

        if (status === 'success') {
          actions.setSubmitting(false);
          actions.resetForm({ values });
        }
      }}
    >
        {({ values, errors }) => {
          const defaultDisabled = values.refCode === 'themeReports' || values.refCode === 'mock';
          return (
            <Form>
              <div className="form-row mb-3 align-items-center">
                <div className="col-auto">
                  <FormHttpSaveBtn res={res} highlight />
                </div>
                <div className="col-auto">
                  {competition && (
                    <Link
                      to={resolveTo('./preview')}
                      className="btn btn-secondary"
                    >
                      <FA icon={faEye} />
                      &nbsp;
                      Preview
                    </Link>
                  )}
                </div>
                {Object.keys(errors).length > 0 && (
                  <div className="col-auto text-danger">
                    There are some problems within the form.
                    &nbsp;
                    {errors.questionSets && `(Question Set: ${errors.questionSets.map((e, i) => {
                      if (e) return values.questionSets[i].refCode;

                      return '';
                    }).filter((m) => m).join(', ')})`}
                  </div>
                )}
                <div className="col"></div>
                {(competition && !defaultDisabled) && (
                  <div className="col-auto">
                    <BtnDel
                      api={{ url: `api/quiz/competitions/${competition._id}` }}
                      onDel={() => {
                        history.push(resolveTo('../'));
                      } } />
                  </div>
                )}
              </div>
              <FormHttpAlert res={res} />

              <TabContainer defaultActiveKey="general">
                <Nav variant="tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="general">General</Nav.Link>
                  </Nav.Item>
                  <FieldArray name="questionSets">
                    {({ push }) => (
                      <>
                        <FieldIterables name="questionSets">
                          {(v, i) => (
                            <Nav.Item
                              key={i}
                            >
                              <Nav.Link
                                eventKey={i}
                                className={errors?.questionSets?.[i]
                                  ? 'text-danger' : ''}
                              >
                                {v.refCode}
                                &nbsp;
                                ({v.draw || 0} / {v.questions?.length || 0})
                              </Nav.Link>
                            </Nav.Item>
                          )}
                        </FieldIterables>
                        <Nav.Item>
                          <Nav.Link
                            onClick={() => (
                              push(defaultQtnSet(values.questionSets.length))
                            )}
                          >
                            <FA icon={faPlus} />
                          </Nav.Link>
                        </Nav.Item>
                      </>
                    )}
                  </FieldArray>
                </Nav>
                <TabContent>
                  <TabPane eventKey="general" unmountOnExit>
                    <h4 className="mt-4">
                      <FA className="mr-3 text-secondary" icon={faCog} />
                      General Settings
                    </h4>
                    <FieldsCompGen fileUploadUrl={fileUploadUrl} defaultDisabled={defaultDisabled} />
                  </TabPane>
                  <FieldArrayIterables name="questionSets">
                    {(v, i, { remove }) => (
                      <TabPane eventKey={i} key={i} unmountOnExit>
                        <div className="row align-items-end">
                          <div className="col-auto">
                            <h4 className="mt-4">
                              <FA
                                className="mr-3 text-secondary"
                                icon={faQuestionCircle} />
                              Question Bank - {v.refCode}
                              &nbsp;
                              ({v.draw || 0} / {v.questions?.length || 0})
                            </h4>
                          </div>
                          <div className="col text-right">
                            <button
                              className="btn btn-link"
                              onClick={() => remove(i)}
                            >
                              <FA icon={faTrashAlt} />
                            </button>
                          </div>
                        </div>
                        <FieldsCompQtnSet
                          idx={i}
                          fileUploadUrl={fileUploadUrl}
                          answered={Boolean(competition?.answers?.length)}
                        />
                      </TabPane>
                    )}
                  </FieldArrayIterables>
                </TabContent>
              </TabContainer>
            </Form>
          );
        }}
    </Formik>
    <ModalAlert
        show={locked != null}
        onHide={() => {
          setLocked(null);
          history.push(resolveTo('../'));
        }}
      >
        {locked ? `${locked.by.name.full.en} 正在使用中，請稍後再試。` : '您還沒有鎖定比賽文件！ 你無法保存！'}
      </ModalAlert>
    </>
  );
};

export default FormCompetition;
